import ErrorToast from "../../components/common/toasts/ErrorToast";
import React, { useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { premiumError, premiumRequest } from "../../services/Premium";
import ProgressBar from "../../components/common/ProgressBar";
import { OrderStatus, PreviewKind } from "./constants";
import { premiumPaths } from "./navigation";
import PremiumLayout from "./components/PremiumLayout";
import FileViewer from "./components/FileViewer";
import SourcePreview from "./components/sources/SourcePreview";
import { sourceContext } from "./sources";
import OrderSteps, {
  getOrderStep,
  orderStepPaths,
} from "./components/OrderSteps";
import OrderCertificatePreview from "./components/OrderCertificatePreview";

/** Osobny podgląd odtwarza dokument po odświeżeniu i wraca do zapisanego zlecenia. */
export default function DocumentPreview({ kind }) {
  const { orderId, documentId } = useParams();
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const returnToDocuments =
    kind === PreviewKind.SOURCE && params.get("from") === "documents";
  const showSource =
    kind === PreviewKind.SOURCE &&
    (params.has("item") || params.has("recipient"));
  const [order, setOrder] = useState(null);
  const [file, setFile] = useState(null);
  const [error, setError] = useState("");
  const [previewReady, setPreviewReady] = useState(false);
  useEffect(() => {
    let active = true;
    setOrder(null);
    setFile(null);
    setError("");
    setPreviewReady(false);
    const api = (suffix = "", options) =>
      premiumRequest(accessToken, `/orders/${orderId}${suffix}`, options);
    const load = async () => {
      const { order: loaded } = await api();
      if (!active) return;
      setOrder(loaded);
      let suffix,
        name,
        options = { responseType: "blob" };
      if (kind === PreviewKind.SOURCE) {
        const document = loaded.documents.find(
          (item) => item.id === documentId,
        );
        if (!document) {
          setError("Nie znaleziono dokumentu w tym zleceniu.");
          return;
        }
        if (showSource) return;
        suffix = `/documents/${encodeURIComponent(documentId)}`;
        name = document.name;
      } else if (kind === PreviewKind.DRAFT) {
        if (loaded.status !== OrderStatus.REVIEW) {
          navigate(premiumPaths.order(orderId), { replace: true });
          return;
        }
        return;
      } else {
        suffix = "/certificate";
        name = "Świadectwo.pdf";
      }
      const blob = await api(suffix, options);
      if (active) setFile({ blob, name });
    };
    load().catch((error) => {
      if (active) setError(premiumError(error));
    });
    return () => {
      active = false;
    };
  }, [accessToken, orderId, documentId, kind, showSource, navigate]);
  const context = showSource && order ? sourceContext(order, params) : null;
  const title =
    kind === PreviewKind.SOURCE
      ? "Dokument źródłowy"
      : kind === PreviewKind.CERTIFICATE
        ? "Gotowe świadectwo"
        : "Podgląd świadectwa";
  return (
    <PremiumLayout
      title={title}
      subtitle={order?.config.exporter.split("\n")[0]}
      actions={
        <Link
          className="btn btn-outline-primary"
          to={
            returnToDocuments
              ? premiumPaths.orderDocuments(orderId)
              : `${premiumPaths.order(orderId)}${context?.returnHash ? `#${context.returnHash}` : ""}`
          }
        >
          {returnToDocuments
            ? "Wróć do dokumentów źródłowych"
            : kind === PreviewKind.CERTIFICATE
              ? "Wróć do zlecenia"
              : "Wróć do danych świadectwa"}
        </Link>
      }
    >
      <OrderSteps
        paths={orderStepPaths(
          order,
          kind === PreviewKind.DRAFT ? { previewReady } : {},
        )}
        step={
          returnToDocuments
            ? 1
            : kind === PreviewKind.DRAFT
              ? 3
              : kind === PreviewKind.CERTIFICATE
                ? 4
                : getOrderStep(order?.status)
        }
      />
      <ErrorToast message={error} />
      {!file &&
        !error &&
        (!showSource || !order) &&
        (kind !== PreviewKind.DRAFT || !order) && (
          <div className="card premium-panel">
            <ProgressBar label="Przygotowanie podglądu" />
          </div>
        )}
      <FileViewer file={file} />
      {kind === PreviewKind.DRAFT && order?.status === OrderStatus.REVIEW && (
        <OrderCertificatePreview
          orderId={order.id}
          revision={order.revision}
          onReadyChange={(ready) => {
            setPreviewReady(ready);
            if (ready)
              setOrder((current) => ({
                ...current,
                previewRevision: current.revision,
              }));
          }}
        />
      )}
      {kind === PreviewKind.DRAFT && order?.status === OrderStatus.REVIEW && (
        <div className="card premium-panel premium-actions">
          <div className="premium-form-actions">
            <Link
              className="btn btn-outline-primary"
              to={premiumPaths.order(orderId)}
            >
              Wróć do poprawiania danych
            </Link>
            <button
              className="btn btn-primary"
              disabled={!previewReady || Boolean(error)}
              onClick={() => navigate(premiumPaths.issue(orderId))}
            >
              Dalej: wystaw i pobierz
            </button>
          </div>
        </div>
      )}
      {showSource &&
        order &&
        !error &&
        (context ? (
          <SourcePreview
            order={order}
            context={context}
            token={accessToken}
            documentId={documentId}
          />
        ) : (
          <p>Nie znaleziono źródła tych danych. Wróć do danych świadectwa.</p>
        ))}
    </PremiumLayout>
  );
}
